import React from 'react';
import { Button } from 'react-bootstrap';

export default function Pagination({ currentPage, totalPages, onPageChange, className }) {
    if (!totalPages || totalPages <= 1) return null;

    const handlePageClick = (page) => {
        if (page !== '...' && page !== currentPage) {
            onPageChange(page);
        }
    };

    const handlePrevious = () => onPageChange(Math.max(1, currentPage - 1));
    const handleNext = () => onPageChange(Math.min(totalPages, currentPage + 1));

    const range = (start, end) => {
        let length = end - start + 1;
        return Array.from({ length }, (_, idx) => idx + start);
    };

    const getPageNumbers = () => {
        const siblingCount = 1;
        const totalPageNumbers = siblingCount * 2 + 5;

        if (totalPages <= totalPageNumbers) {
            return range(1, totalPages);
        }

        const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
        const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

        const shouldShowLeftDots = leftSiblingIndex > 2;
        const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

        const firstPageIndex = 1;
        const lastPageIndex = totalPages;

        if (!shouldShowLeftDots && shouldShowRightDots) {
            let leftItemCount = 3 + 2 * siblingCount;
            let leftRange = range(1, leftItemCount);
            return [...leftRange, '...', lastPageIndex];
        }

        if (shouldShowLeftDots && !shouldShowRightDots) {
            let rightItemCount = 3 + 2 * siblingCount;
            let rightRange = range(totalPages - rightItemCount + 1, totalPages);
            return [firstPageIndex, '...', ...rightRange];
        }

        if (shouldShowLeftDots && shouldShowRightDots) {
            let middleRange = range(leftSiblingIndex, rightSiblingIndex);
            return [firstPageIndex, '...', ...middleRange, '...', lastPageIndex];
        }
    };

    const pages = getPageNumbers();

    return (
        <div className={className || 'd-flex justify-content-center align-items-center gap-2 mt-4'}>
            <Button
                size="sm"
                variant="outline-secondary"
                disabled={currentPage <= 1}
                onClick={handlePrevious}
                style={{
                    borderRadius: '8px',
                    borderColor: '#ddd',
                    color: currentPage <= 1 ? '#ccc' : '#555',
                    padding: '6px 12px',
                    fontSize: 13,
                    fontWeight: 500,
                    backgroundColor: '#fff',
                    transition: 'all 0.2s',
                }}
            >
                ‹ Précédent
            </Button>

            <div className="d-flex align-items-center gap-1">
                {pages.map((page, idx) => {
                    const isCurrent = page === currentPage;
                    const isDots = page === '...';

                    return (
                        <button
                            key={idx}
                            disabled={isDots}
                            onClick={() => handlePageClick(page)}
                            style={{
                                width: 34,
                                height: 34,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: isCurrent ? '1px solid #8B0000' : '1px solid transparent',
                                borderRadius: '8px',
                                backgroundColor: isCurrent ? '#8B0000' : 'transparent',
                                color: isCurrent ? '#fff' : isDots ? '#aaa' : '#555',
                                fontSize: 13,
                                fontWeight: isCurrent ? '600' : '500',
                                cursor: isDots ? 'default' : 'pointer',
                                transition: 'all 0.2s',
                                outline: 'none',
                            }}
                            onMouseEnter={(e) => {
                                if (!isCurrent && !isDots) {
                                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                                    e.currentTarget.style.color = '#8B0000';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isCurrent && !isDots) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#555';
                                }
                            }}
                        >
                            {page}
                        </button>
                    );
                })}
            </div>

            <Button
                size="sm"
                variant="outline-secondary"
                disabled={currentPage >= totalPages}
                onClick={handleNext}
                style={{
                    borderRadius: '8px',
                    borderColor: '#ddd',
                    color: currentPage >= totalPages ? '#ccc' : '#555',
                    padding: '6px 12px',
                    fontSize: 13,
                    fontWeight: 500,
                    backgroundColor: '#fff',
                    transition: 'all 0.2s',
                }}
            >
                Suivant ›
            </Button>
        </div>
    );
}
